/**
 * knowledge-base-detail-page.tsx — Trang MỘT kho tri thức (route /ai/knowledge/:baseId[?tab=docs|qa|materials]).
 *
 * - Đầu trang: quay lại danh sách kho, icon sách, tên kho + bút sửa tên / mô tả.
 * - Kho trống: màn chào "Xây dựng kho kiến thức AI của bạn" + 4 thẻ (Tải tài liệu lên / Thêm hỏi đáp / Thêm ảnh và video /
 *   Đồng bộ nội dung) + "Bỏ qua hướng dẫn này".
 * - Kho có nội dung (hoặc đã bỏ qua hướng dẫn): 3 tab Tài liệu (n) | Hỏi đáp (n) | Ảnh và video (n); mỗi tab có cây RIÊNG
 *   (thư mục Tài liệu · danh mục Hỏi đáp · nhóm Ảnh và video) — lựa chọn cây nhớ riêng theo tab.
 */
import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, BookOpen, FileUp, Image as ImageIcon, MessageCircleQuestion, Pencil, RefreshCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useKnowledgeBase } from '@/hooks/use-knowledge-bases'
import { CreateKnowledgeDialog, type AddKnowledgeTab } from '../add-knowledge-dialog'
import { BaseFormDialog, useKnowledgePerms } from './knowledge-bases-page'
import { KbDocsTab } from './kb-docs-tab'
import { KbQaTab } from './kb-qa-tab'
import { KbMaterialsTab } from './kb-materials-tab'
import { KbSyncDialog } from './kb-sync-dialog'
import { isBaseEmpty, KB_TABS, KNOWLEDGE_BASES_PATH, type KbTab } from './kb-lib'

const SKIP_KEY = 'ecdp:kb-skip-guide'

function readSkipped(id: string): boolean {
  try { return (JSON.parse(localStorage.getItem(SKIP_KEY) ?? '[]') as string[]).includes(id) } catch { return false }
}
function writeSkipped(id: string) {
  try {
    const cur = JSON.parse(localStorage.getItem(SKIP_KEY) ?? '[]') as string[]
    if (!cur.includes(id)) localStorage.setItem(SKIP_KEY, JSON.stringify([...cur, id].slice(-200)))
  } catch { /* bỏ qua */ }
}

export function KnowledgeBaseDetailPage() {
  const { t } = useTranslation('aiKnow')
  const { baseId = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const perms = useKnowledgePerms()
  const { data: base, isLoading, isError } = useKnowledgeBase(baseId)
  const tabParam = params.get('tab') as KbTab | null
  const tab: KbTab = tabParam && KB_TABS.includes(tabParam) ? tabParam : 'docs'
  const [skipped, setSkipped] = useState(() => readSkipped(baseId))
  // Danh mục / thư mục / nhóm đang chọn — mỗi tab một cây nên nhớ riêng.
  const [picked, setPicked] = useState<Record<KbTab, string>>({ docs: '', qa: '', materials: '' })
  const pick = (k: KbTab) => (id: string) => setPicked((p) => ({ ...p, [k]: id }))
  const [editOpen, setEditOpen] = useState(false)
  const [addTab, setAddTab] = useState<AddKnowledgeTab | null>(null)
  const [syncOpen, setSyncOpen] = useState(false)
  const [openQa, setOpenQa] = useState(false)
  const [openMat, setOpenMat] = useState(false)
  const qaHandled = useCallback(() => setOpenQa(false), [])
  const matHandled = useCallback(() => setOpenMat(false), [])

  const setTab = (k: KbTab) => {
    const next = new URLSearchParams(params)
    next.set('tab', k)
    setParams(next, { replace: true })
  }
  const skipGuide = () => { writeSkipped(baseId); setSkipped(true) }

  if (isLoading) return <Loading label={t('detail.loading')} />
  if (isError || !base) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(KNOWLEDGE_BASES_PATH)}><ArrowLeft className="h-4 w-4" /> {t('detail.backToList')}</Button>
        <ErrorState message={t('detail.notFound')} />
      </div>
    )
  }

  const empty = isBaseEmpty(base)
  // Màn chào chỉ gồm các cách THÊM nội dung → thiếu knowledge.create thì vào thẳng các tab (chỉ xem).
  const showGuide = empty && !skipped && !tabParam && perms.canCreate

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" title={t('detail.listTitle')} aria-label={t('detail.backAria')} onClick={() => navigate(KNOWLEDGE_BASES_PATH)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><BookOpen className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h1 className="truncate text-lg font-medium" title={base.name}>{base.name}</h1>
            {perms.canUpdate && (
              <Button variant="ghost" size="icon" className="h-7 w-7" title={t('detail.editTitle')} aria-label={t('detail.editAria')} onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {base.description && <p className="truncate text-xs text-muted-foreground">{base.description}</p>}
        </div>
        <span className="text-xs text-muted-foreground" title={[...base.bots, ...base.allBasesBots].map((b) => b.name).join(', ')}>
          <Trans t={t} i18nKey="detail.botCount" values={{ n: base.botCount }} components={{ b: <b className="text-foreground" /> }} />
        </span>
      </div>

      {showGuide ? (
        <WelcomeGuide
          onUpload={() => { skipGuide(); setTab('docs'); setAddTab('file') }}
          onQa={() => { skipGuide(); setTab('qa'); setOpenQa(true) }}
          onMaterial={() => { skipGuide(); setTab('materials'); setOpenMat(true) }}
          onSync={() => { skipGuide(); setTab('docs'); setSyncOpen(true) }}
          onSkip={() => { skipGuide(); setTab('docs') }}
        />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as KbTab)}>
          <TabsList>
            <TabsTrigger value="docs">{t('detail.tabs.docs', { n: base.articleCount.toLocaleString(currentLocale()) })}</TabsTrigger>
            <TabsTrigger value="qa">{t('detail.tabs.qa', { n: base.qaCount.toLocaleString(currentLocale()) })}</TabsTrigger>
            <TabsTrigger value="materials">{t('detail.tabs.materials', { n: base.materialCount.toLocaleString(currentLocale()) })}</TabsTrigger>
          </TabsList>
          <TabsContent value="docs" className="pt-2">
            <KbDocsTab baseId={base.id} perms={perms} categoryId={picked.docs} onCategory={pick('docs')} onAdd={setAddTab} onSync={() => setSyncOpen(true)} />
          </TabsContent>
          <TabsContent value="qa" className="pt-2">
            <KbQaTab baseId={base.id} perms={perms} categoryId={picked.qa} onCategory={pick('qa')} openAdd={openQa} onOpenAddHandled={qaHandled} />
          </TabsContent>
          <TabsContent value="materials" className="pt-2">
            <KbMaterialsTab baseId={base.id} perms={perms} categoryId={picked.materials} onCategory={pick('materials')} openUpload={openMat} onOpenUploadHandled={matHandled} />
          </TabsContent>
        </Tabs>
      )}

      {editOpen && <BaseFormDialog base={base} onClose={() => setEditOpen(false)} />}
      {syncOpen && <KbSyncDialog onClose={() => setSyncOpen(false)} onPick={(k) => setAddTab(k)} />}
      {addTab && (
        <CreateKnowledgeDialog
          knowledgeBaseId={base.id}
          defaultCategoryId={picked.docs /* hộp thêm mở từ tab Tài liệu → thư mục Tài liệu */}
          tabs={addTab === 'link' || addTab === 'sheet' ? ['link', 'sheet'] : [addTab]}
          initialTab={addTab}
          onClose={() => setAddTab(null)}
        />
      )}
    </div>
  )
}

function WelcomeGuide({ onUpload, onQa, onMaterial, onSync, onSkip }: {
  onUpload: () => void; onQa: () => void; onMaterial: () => void; onSync: () => void; onSkip: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const cards = [
    { key: 'upload', title: t('detail.welcome.upload'), desc: t('detail.welcome.uploadDesc'), icon: <FileUp className="h-6 w-6" />, tint: 'bg-primary/10 text-primary', onClick: onUpload },
    { key: 'qa', title: t('detail.welcome.qa'), desc: t('detail.welcome.qaDesc'), icon: <MessageCircleQuestion className="h-6 w-6" />, tint: 'bg-success/10 text-success', onClick: onQa },
    { key: 'material', title: t('detail.welcome.material'), desc: t('detail.welcome.materialDesc'), icon: <ImageIcon className="h-6 w-6" />, tint: 'bg-warning/10 text-warning', onClick: onMaterial },
    { key: 'sync', title: t('detail.welcome.sync'), desc: t('detail.welcome.syncDesc'), icon: <RefreshCcw className="h-6 w-6" />, tint: 'bg-accent text-accent-foreground', onClick: onSync },
  ]
  return (
    <div className="rounded-xl border bg-card px-6 py-10">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-xl font-semibold">{t('detail.welcome.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('detail.welcome.subtitle')}</p>
      </div>
      <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <button
            key={c.key} type="button" onClick={c.onClick}
            className="flex flex-col items-start gap-3 rounded-xl border p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow"
          >
            <span className={cn('flex h-12 w-12 items-center justify-center rounded-xl', c.tint)}>{c.icon}</span>
            <span className="font-medium">{c.title}</span>
            <span className="text-xs leading-relaxed text-muted-foreground">{c.desc}</span>
          </button>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="text-sm text-primary hover:underline" onClick={onSkip}>{t('detail.welcome.skip')}</button>
      </div>
    </div>
  )
}
